import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Gift, ArrowRight } from "lucide-react";
import { Button } from "../ui/button";
import { DEPOSIT_BONUS_TIERS, getDepositBonus } from "../../data/fees";
import { useLanguage } from "../../contexts/LanguageContext";

const COPY = {
    en: { eyebrow: "Top-up bonus", h1: "Fund more,", h2: "get more — instantly.", sub: "Every qualifying top-up is boosted the moment it confirms on-chain. The extra credit lands straight in your balance — no waiting, no claim.", from: "From", getPrefix: "Top up", getMid: "and get", credited: "credited instantly", yourTopup: "Your top-up", instantCredit: "Instant credit", total: "Total balance", cta: "Start funding", note: "Bonus applies per confirmed top-up. Configurable by tier." },
    tr: { eyebrow: "Yükleme bonusu", h1: "Daha çok yükle,", h2: "anında daha fazlasını al.", sub: "Uygun her yükleme, zincirde onaylandığı an artırılır. Ekstra kredi doğrudan bakiyene geçer — bekleme yok, talep yok.", from: "Şununla başlar", getPrefix: "Yükle", getMid: "ve al", credited: "anında bakiyene", yourTopup: "Yüklemen", instantCredit: "Anında kredi", total: "Toplam bakiye", cta: "Yüklemeye başla", note: "Bonus her onaylı yüklemede geçerlidir. Kademe bazlı ayarlanabilir." },
    es: { eyebrow: "Bono de recarga", h1: "Recarga más,", h2: "recibe más — al instante.", sub: "Cada recarga válida se potencia en cuanto se confirma en la cadena. El crédito extra entra directo a tu saldo — sin esperas.", from: "Desde", getPrefix: "Recarga", getMid: "y obtén", credited: "acreditado al instante", yourTopup: "Tu recarga", instantCredit: "Crédito instantáneo", total: "Saldo total", cta: "Empezar a recargar", note: "El bono aplica por recarga confirmada. Configurable por nivel." },
    pt: { eyebrow: "Bônus de recarga", h1: "Abasteça mais,", h2: "receba mais — na hora.", sub: "Cada recarga elegível é turbinada assim que confirma on-chain. O crédito extra cai direto no seu saldo — sem espera.", from: "A partir de", getPrefix: "Recarregue", getMid: "e ganhe", credited: "creditado na hora", yourTopup: "Sua recarga", instantCredit: "Crédito instantâneo", total: "Saldo total", cta: "Começar a abastecer", note: "O bônus vale por recarga confirmada. Configurável por nível." },
    ru: { eyebrow: "Бонус за пополнение", h1: "Пополняй больше —", h2: "получай больше сразу.", sub: "Каждое подходящее пополнение усиливается в момент подтверждения в сети. Доп. кредит сразу попадает на баланс — без ожидания.", from: "От", getPrefix: "Пополни", getMid: "и получи", credited: "мгновенно на баланс", yourTopup: "Пополнение", instantCredit: "Мгновенный кредит", total: "Итоговый баланс", cta: "Начать пополнение", note: "Бонус за каждое подтверждённое пополнение. Настраивается по уровням." },
};

const PRESETS = [2500, 10000, 25000, 50000];
const fmt = (n) => "$" + Math.round(n).toLocaleString("en-US");

export default function DepositBonusSection() {
    const { language } = useLanguage();
    const c = COPY[language] || COPY.en;
    const [amount, setAmount] = useState(25000);
    const { bonusPct, bonus } = useMemo(() => getDepositBonus(amount), [amount]);

    return (
        <section id="bonus" data-testid="deposit-bonus-section" className="py-24 md:py-32 border-t border-white/[0.06]">
            <div className="container-skala grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
                <div>
                    <div className="inline-flex items-center gap-2 mb-5">
                        <span className="h-px w-8 bg-highlight" />
                        <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-highlight">{c.eyebrow}</p>
                    </div>
                    <h2 className="font-display font-semibold text-4xl md:text-6xl tracking-tight leading-[1.03]">
                        {c.h1} <span className="serif-accent text-foreground/85">{c.h2}</span>
                    </h2>
                    <p className="text-muted-foreground text-base md:text-lg mt-5 max-w-xl leading-relaxed">{c.sub}</p>

                    <div className="mt-10 divide-y divide-white/[0.07] border-t border-white/[0.1]">
                        {DEPOSIT_BONUS_TIERS.map((tI) => (
                            <div key={tI.min} className="flex items-center justify-between py-4">
                                <span className="font-mono text-sm text-muted-foreground">{c.from} <span className="text-foreground">{fmt(tI.min)}</span></span>
                                <span className="font-display text-2xl text-highlight">+{tI.bonusPct}%</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="rounded-xl border border-white/[0.1] bg-card p-7 md:p-9">
                    <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">
                        <Gift className="w-4 h-4 text-highlight" /> {c.instantCredit}
                    </div>

                    <div className="mt-6 flex flex-wrap gap-2">
                        {PRESETS.map((p) => (
                            <button
                                key={p}
                                onClick={() => setAmount(p)}
                                data-testid={`bonus-preset-${p}`}
                                className={`px-3.5 py-2 rounded-md text-sm font-mono border transition-colors ${amount === p ? "border-highlight/60 bg-highlight/10 text-highlight" : "border-white/10 text-muted-foreground hover:text-foreground hover:border-white/20"}`}
                            >
                                {fmt(p)}
                            </button>
                        ))}
                    </div>

                    <input
                        type="range"
                        min={500}
                        max={75000}
                        step={500}
                        value={amount}
                        onChange={(e) => setAmount(Number(e.target.value))}
                        data-testid="bonus-slider"
                        className="w-full mt-6 accent-[hsl(158_42%_46%)]"
                    />

                    <div className="mt-6 grid grid-cols-3 gap-3 text-center">
                        <div className="rounded-lg border border-white/[0.08] p-4">
                            <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">{c.yourTopup}</p>
                            <p className="font-display text-xl md:text-2xl mt-1">{fmt(amount)}</p>
                        </div>
                        <div className="rounded-lg border border-highlight/30 bg-highlight/[0.06] p-4">
                            <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-highlight">+{bonusPct}%</p>
                            <p className="font-display text-xl md:text-2xl mt-1 text-highlight" data-testid="bonus-amount">{fmt(bonus)}</p>
                        </div>
                        <div className="rounded-lg border border-white/[0.08] p-4">
                            <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground">{c.total}</p>
                            <p className="font-display text-xl md:text-2xl mt-1">{fmt(amount + bonus)}</p>
                        </div>
                    </div>

                    <Link to="/signup" data-testid="bonus-cta">
                        <Button className="btn-highlight w-full h-12 rounded-md mt-6">{c.cta}<ArrowRight className="ml-2 w-4 h-4" /></Button>
                    </Link>
                    <p className="text-[11px] text-muted-foreground font-mono mt-3 text-center">{c.note}</p>
                </div>
            </div>
        </section>
    );
}
